import { useCallback, useEffect, useState } from "react";

import {
  AiConnector,
  AiConnectorHealth,
  AiDifficultyProfile,
  AiEffectiveConfig,
  AiGatewayRoute,
  AiModel,
  aiGatewayService,
} from "services/aiGatewayService";

/**
 * Loads the six reads the AI Gateway page is built from.
 *
 * They are fetched together and reloaded together because they are not independent: binding a
 * route changes the overview, adding a connector changes the model list, and saving a difficulty
 * profile changes the tier column on the routing table. Reloading only the panel that was edited
 * would leave the other tabs quietly describing the previous state.
 *
 * Health is the one read that changes on its own rather than in response to an edit, so the
 * Refresh button is how it is re-checked — the page does not poll.
 *
 * The axios interceptor already fires an error toast on failure; `error` here only drives the
 * inline empty/error state.
 */
export function useAiGateway() {
  const [connectors, setConnectors] = useState<AiConnector[]>([]);
  const [health, setHealth] = useState<AiConnectorHealth[]>([]);
  const [models, setModels] = useState<AiModel[]>([]);
  const [routes, setRoutes] = useState<AiGatewayRoute[]>([]);
  const [profile, setProfile] = useState<AiDifficultyProfile | null>(null);
  const [overview, setOverview] = useState<AiEffectiveConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [
        loadedConnectors,
        loadedHealth,
        loadedModels,
        loadedRoutes,
        loadedProfile,
        loadedOverview,
      ] = await Promise.all([
        aiGatewayService.getConnectors(),
        aiGatewayService.getConnectorHealth(),
        aiGatewayService.getModels(),
        aiGatewayService.getRoutes(),
        aiGatewayService.getDifficultyProfile(),
        aiGatewayService.getOverview(),
      ]);
      setConnectors(loadedConnectors);
      setHealth(loadedHealth);
      setModels(loadedModels);
      setRoutes(loadedRoutes);
      setProfile(loadedProfile);
      setOverview(loadedOverview);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "load-failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return {
    connectors,
    health,
    models,
    routes,
    profile,
    overview,
    loading,
    error,
    reload,
  };
}
